import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import {
  Loader2, RefreshCw, Search, Plus, Package, Check, AlertTriangle, Edit3, X as XIcon, Save, ShieldCheck,
} from "lucide-react";
import ComposeModal from "@/components/ComposeModal";
import { useAuth } from "@/context/AuthContext";
import { useLivePoll } from "@/hooks/useLivePoll";

/**
 * PartsLibrary — 1:1 with the mobile app's Parts Library screen.
 *
 * Layout:
 *   Header with "New part" button
 *   Search bar   "Search by name, number, category…"
 *   Filter chips: [ALL] [PENDING] [VERIFIED]
 *   Card grid:   Part name · Category · Used for · Status pill · Edit button
 */
const FILTERS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "verified", label: "Verified" },
];

export default function PartsLibrary() {
  const { user } = useAuth();
  const [parts, setParts] = useState(null);
  const [pending, setPending] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [edit, setEdit] = useState(null);

  const reload = async () => {
    setBusy(true); setErr("");
    try {
      const [a, p] = await Promise.allSettled([
        api.get("/parts-library"),
        api.get("/parts-library/pending"),
      ]);
      const list = (r) => {
        if (r.status !== "fulfilled") return [];
        const d = r.value.data;
        return Array.isArray(d) ? d : d?.items || d?.results || d?.data || [];
      };
      setParts(list(a));
      setPending(list(p));
    } catch (ex) { setErr(apiError(ex)); }
    finally { setBusy(false); }
  };
  useEffect(() => { reload(); }, []);
  // Live-sync so any part added on the mobile app (via a Breakdown or PPF)
  // appears here within 5 s — no manual refresh.
  useLivePoll(reload, 5000, true);

  const canVerify = String(user?.role || "").toLowerCase().match(/admin|control|owner|planner|superintendent/) || user?.is_admin;

  const counts = useMemo(() => ({
    all: (parts || []).length,
    pending: (pending || []).length,
    verified: (parts || []).filter((p) => statusOf(p) === "verified").length,
  }), [parts, pending]);

  const filtered = useMemo(() => {
    let list;
    if (filter === "pending") list = pending || [];
    else if (filter === "verified") list = (parts || []).filter((p) => statusOf(p) === "verified");
    else list = parts || [];
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((p) => {
        const hay = `${p.part_name || ""} ${p.part_number || ""} ${p.category || ""} ${(p.asset_ids || []).join(" ")} ${p.last_used_for || ""} ${p.store_location || ""}`.toLowerCase();
        return hay.includes(s);
      });
    }
    return list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [parts, pending, filter, q]);

  const approve = async (p) => {
    try { await api.post(`/parts-library/${p.id || p._id}/approve`); reload(); }
    catch (ex) { alert(apiError(ex)); }
  };

  return (
    <div className="space-y-4" data-testid="parts-library">
      {/* Header row */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">Parts Library</div>
          <div className="font-display font-black text-2xl md:text-3xl tracking-tight leading-none mt-1">
            {counts.all} part{counts.all === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-sm btn-ghost" onClick={reload} disabled={busy} data-testid="parts-refresh">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
          <button className="btn btn-sm btn-primary" onClick={() => setShowNew(true)} data-testid="parts-new">
            <Plus className="w-4 h-4" /> New Part
          </button>
        </div>
      </div>

      {/* Search bar — matches app copy exactly */}
      <div className="panel-flat px-3 flex items-center gap-2">
        <Search className="w-4 h-4 text-muted shrink-0" />
        <input
          className="w-full py-3 bg-transparent outline-none text-base"
          placeholder="Search by name, number, category..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="parts-search"
        />
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2" data-testid="parts-filters">
        {FILTERS.map((f) => {
          const on = f.id === filter;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-black uppercase tracking-widest transition-colors ${
                on ? "text-black" : "text-white/70 hover:text-white"
              }`}
              style={on ? { background: "var(--primary)" } : { background: "var(--surface-2)", border: "1px solid var(--border)" }}
              data-testid={`parts-filter-${f.id}`}
            >
              {f.label}
              <span className={`tabular-nums text-[10px] ${on ? "text-black/60" : "text-white/50"}`}>{counts[f.id]}</span>
            </button>
          );
        })}
      </div>

      {err && <div className="pill pill-red !text-sm !py-2 !px-3 justify-start"><AlertTriangle className="w-3.5 h-3.5" /> {err}</div>}

      {/* Cards */}
      {parts === null ? (
        <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading parts library…
        </div>
      ) : filtered.length === 0 ? (
        <div className="panel-flat p-10 text-center text-muted" data-testid="parts-empty">
          <Package className="w-8 h-8 mx-auto mb-2 opacity-60" />
          {filter === "pending" ? "No parts pending verification." : filter === "verified" ? "No verified parts yet." : "No parts in the library yet."}
        </div>
      ) : (
        <div className="grid gap-2" data-testid="parts-list">
          {filtered.map((p) => {
            const status = statusOf(p);
            const st = STATUS_TONE[status] || STATUS_TONE.pending;
            const id = p.id || p._id;
            const usedFor = p.last_used_for || (p.asset_ids && p.asset_ids[0]) || null;
            return (
              <div key={id} className="panel p-4 flex items-center gap-4" data-testid={`part-card-${id}`}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                     style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
                  <Package className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-black text-base truncate">{p.part_name || "Part"}</div>
                  <div className="text-[11px] text-muted font-mono-tight mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                    {p.category && <span>{p.category}</span>}
                    {p.part_number && <span>#{p.part_number}</span>}
                    {usedFor && <span>Used for: <span className="text-white/80">{usedFor}</span></span>}
                    {p.store_location && <span>· {p.store_location}</span>}
                  </div>
                </div>
                <span className="pill !text-[10px] !py-0.5 !px-2 uppercase font-black tracking-widest"
                      style={{ background: st.bg, border: `1px solid ${st.border}`, color: st.color }}
                      data-testid={`part-status-${id}`}>
                  {st.label}
                </span>
                {status === "pending" && canVerify && (
                  <button className="btn btn-sm" onClick={() => approve(p)} title="Verify part" data-testid={`part-verify-${id}`}>
                    <ShieldCheck className="w-4 h-4" /> Verify
                  </button>
                )}
                <button className="btn btn-sm btn-ghost" onClick={() => setEdit(p)} title="Edit" data-testid={`part-edit-${id}`}>
                  <Edit3 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <ComposeModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={reload}
        endpoint="/parts-library"
        title="Add Part"
        fields={[
          { name: "part_name", label: "Part name", required: true, placeholder: "Bucket tooth" },
          { name: "part_number", label: "Part number", placeholder: "1U-3352WTL" },
          { name: "category", label: "Category", placeholder: "GET / Filter / Belt / Hose" },
          { name: "last_used_for", label: "Used for (Asset ID)", placeholder: "DIG-12" },
          { name: "store_location", label: "Store location", placeholder: "Warehouse A · Bin 12" },
        ]}
        testid="parts-create"
      />

      {edit && (
        <EditPartModal
          part={edit}
          onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); reload(); }}
        />
      )}
    </div>
  );
}

/* ---------------- Edit modal ---------------- */
function EditPartModal({ part, onClose, onSaved }) {
  const [form, setForm] = useState({
    part_name: part.part_name || "",
    part_number: part.part_number || "",
    category: part.category || "",
    last_used_for: part.last_used_for || "",
    store_location: part.store_location || "",
    asset_ids: part.asset_ids || [],
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await api.patch(`/parts-library/${part.id || part._id}`, form);
      setMsg({ type: "ok", text: "Saved." });
      setTimeout(onSaved, 400);
    } catch (ex) {
      setBusy(false);
      setMsg({ type: "err", text: apiError(ex) });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="parts-edit-modal">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }} onClick={onClose} />
      <div className="relative panel-heavy w-full max-w-lg p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
               style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
            <Package className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-muted">Edit part</div>
            <div className="font-display font-black text-lg tracking-tight">{part.part_name || "Part"}</div>
          </div>
          <button className="btn btn-sm btn-ghost !px-2" onClick={onClose}><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          {[
            ["part_name", "Part name"],
            ["part_number", "Part number"],
            ["category", "Category"],
            ["last_used_for", "Used for (Asset ID)"],
            ["store_location", "Store location"],
          ].map(([k, label]) => (
            <div key={k} className="field">
              <label>{label}</label>
              <input className="input" value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`parts-edit-${k}`} />
            </div>
          ))}
          {msg && (
            <div className={`pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start`}>
              {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} {msg.text}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="parts-edit-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- helpers ---------------- */
const STATUS_TONE = {
  verified: { label: "VERIFIED", bg: "#0a2a12", border: "#1e6b32", color: "#4ade80" },
  pending:  { label: "PENDING",  bg: "#4a2a06", border: "#f58220", color: "#ffb347" },
};
function statusOf(p) {
  const s = String(p.status || "").toLowerCase();
  if (s.includes("verified") || s.includes("approved") || p.verified === true) return "verified";
  return "pending";
}
